import React, { useMemo } from 'react';
import BaseReactFlow from './BaseReactFlow';
import EnergyMeterViewerOverlay from '../../../components/energy-meter/EnergyMeterViewerOverlay';
import { usePageGroupIdViewer } from '../../../hooks/usePageGroupIdViewer';
import { useTranslation } from '../../../hooks/useTranslation';

const ProcessMapFlow = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onNodeClick,
  nodeTypes,
  edgeTypes,
  energyMeter,
  processId,
  safeRemainingHeight,
  isLoading,
  showPageGroupIdViewer = true,
  nodesForPageGroupId,
  fitView = true,
  // Custom classes/styles
  appContainerClassName = 'app-container',
  appContainerStyle,
  contentWrapperClassName = 'content-wrapper',
  contentWrapperStyle,
  flowContainerClassName = 'flow-container',
  flowContainerStyle,
  // Edit mode props
  onNodeDragStop,
  onNodeContextMenu,
  snapToGrid,
  snapGrid,
  preventScrolling = false,
  children,
  ...rest
}) => {
  const t = useTranslation();

  const nodesToUseForPageGroupId = nodesForPageGroupId || nodes;
  const pageGroupIdViewer = usePageGroupIdViewer(nodesToUseForPageGroupId);

  // If energyMeter is active, enrich nodes dynamically
  const nodesWithEM = useMemo(() => {
    if (!energyMeter || !energyMeter.active) return nodes;
    const version = energyMeter.processVersion;
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        em: {
          active: energyMeter.active,
          mode: energyMeter.mode,
          stats: energyMeter.stats,
          promptActivity: energyMeter.promptActivity,
          popoverActivity: energyMeter.popoverActivity,
          onOpenPrompt: energyMeter.openPrompt,
          onOpenPopover: energyMeter.openPopover,
          processId,
          version,
          lastUpdated: energyMeter.lastUpdated,
        },
      },
    }));
  }, [nodes, energyMeter, processId]);

  const defaultAppContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: safeRemainingHeight,
    minHeight: safeRemainingHeight || 588,
    backgroundColor: '#f8f9fa',
    ...appContainerStyle,
  };

  const defaultContentWrapperStyle = {
    display: 'flex',
    flex: 1,
    border: '1px solid #1A192B',
    borderTop: 'none',
    ...contentWrapperStyle,
  };

  const defaultFlowContainerStyle = {
    flex: 1,
    backgroundColor: '#ffffff',
    position: 'relative',
    ...flowContainerStyle,
  };

  return (
    <>
      <div className={appContainerClassName} style={defaultAppContainerStyle}>
        <div className={contentWrapperClassName} style={defaultContentWrapperStyle}>
          <div className={flowContainerClassName} style={defaultFlowContainerStyle}>
            <BaseReactFlow
              nodes={nodesWithEM}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              zoomOnScroll={false}
              zoomOnPinch={false}
              panOnDrag={false}
              panOnScroll={false}
              autoPanOnConnect={false}
              autoPanOnNodeDrag={false}
              minZoom={0.1}
              maxZoom={0.6}
              fitView={fitView}
              onNodeDragStop={onNodeDragStop}
              onNodeContextMenu={onNodeContextMenu}
              snapToGrid={snapToGrid}
              snapGrid={snapGrid}
              preventScrolling={preventScrolling}
              {...rest}
            />
            {energyMeter && energyMeter.active && (
              <EnergyMeterViewerOverlay energyMeter={energyMeter} processId={processId} />
            )}
            {isLoading && (
              <div className="loading-overlay">
                <div className="spinner"></div>
                <div>{t('loading')}</div>
              </div>
            )}
            {children}
          </div>
          {showPageGroupIdViewer && pageGroupIdViewer}
        </div>
      </div>
    </>
  );
};

export default ProcessMapFlow;
